import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TooltipDirective } from '@catch/directives';
import {
  ButtonComponent,
  EmptyPlaceholderComponent,
  ModalComponent,
  SkillLevelTagComponent,
  SkillLevelTagOption,
  TableSearchInputComponent,
} from '@catch/general-ui';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faPlus,
  faTrash,
  faListCheck,
  faSort,
  faSortUp,
  faSortDown,
} from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgSelectModule } from '@ng-select/ng-select';
import { TableModule } from 'primeng/table';
import { ApplicantSkillLevel } from '../../../../../enums/applicant-skill-level.enum';
import {
  PLATFORM_PROFILE_SKILL_LEVELS,
  PlatformProfileSkill,
} from '../../../../models/platform.models';
import { PlatformSkillsCatalogService } from '../../../../services/platform-skills-catalog.service';

@Component({
  selector: 'app-platform-profile-skills',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    NgSelectModule,
    TableModule,
    ButtonComponent,
    EmptyPlaceholderComponent,
    ModalComponent,
    SkillLevelTagComponent,
    TableSearchInputComponent,
    FaIconComponent,
    TooltipDirective,
  ],
  templateUrl: './platform-profile-skills.component.html',
  styleUrl: './platform-profile-skills.component.scss',
})
export class PlatformProfileSkillsComponent {
  private readonly catalog = inject(PlatformSkillsCatalogService);

  readonly form = input.required<FormGroup>();
  readonly duplicateSkill = output<void>();

  readonly skillLevelOptions: SkillLevelTagOption[] = PLATFORM_PROFILE_SKILL_LEVELS.map(
    (level) => ({
      value: level,
      label: `ct-library.constants.skill-level.${level}`,
    })
  );
  readonly newSkillName = new FormControl<string | null>(null);
  readonly newSkillLevel = new FormControl<ApplicantSkillLevel>(
    ApplicantSkillLevel.EXISTING,
    { nonNullable: true }
  );
  readonly icons = {
    plus: faPlus,
    trash: faTrash,
    empty: faListCheck,
    sort: faSort,
    sortUp: faSortUp,
    sortDown: faSortDown,
  };
  readonly skills = signal<PlatformProfileSkill[]>([]);
  readonly catalogTitles = signal<string[]>([]);
  readonly searchQuery = signal<string | null>(null);
  readonly addModal = viewChild.required<ModalComponent>('addModal');

  readonly availableSkillNames = computed(() => {
    const selected = new Set(
      this.skills().map((skill) => skill.name.toLowerCase())
    );
    return this.catalogTitles().filter(
      (title) => !selected.has(title.toLowerCase())
    );
  });

  readonly visibleSkills = computed(() => {
    const search = this.searchQuery()?.trim().toLowerCase() ?? '';
    if (!search) {
      return this.skills();
    }
    return this.skills().filter(
      (skill) =>
        skill.name.toLowerCase().includes(search) ||
        skill.level.toLowerCase().includes(search)
    );
  });

  constructor() {
    effect((onCleanup) => {
      const form = this.form();
      this.skills.set(
        (form.controls['skills'].value as PlatformProfileSkill[]) ?? []
      );
      const sub = form.controls['skills'].valueChanges.subscribe((value) => {
        this.skills.set((value as PlatformProfileSkill[]) ?? []);
      });
      onCleanup(() => sub.unsubscribe());
    });

    effect((onCleanup) => {
      const sub = this.catalog.getSkills().subscribe((skills) => {
        this.catalogTitles.set(skills.map((skill) => skill.title));
      });
      onCleanup(() => sub.unsubscribe());
    });
  }

  /**
   * Track function for skill table rows.
   * @param _index - Row index
   * @param skill - Skill row
   * @returns Stable row identity
   */
  trackBySkill(_index: number, skill: PlatformProfileSkill): string {
    return skill.name;
  }

  /**
   * Updates the free-text search for the skills table.
   * @param search - Search query or null
   */
  onSearch(search: string | null): void {
    this.searchQuery.set(search);
  }

  /**
   * Returns the translation key for a skill level.
   * @param level - Skill level value
   * @returns Translation key
   */
  skillLevelLabel(level: string): string {
    return `ct-library.constants.skill-level.${level}`;
  }

  /**
   * Opens the add-skill modal with a fresh form.
   */
  openAddModal(): void {
    this.newSkillName.setValue(null);
    this.newSkillLevel.setValue(ApplicantSkillLevel.EXISTING);
    this.addModal().open();
  }

  /**
   * Closes the add-skill modal.
   */
  closeAddModal(): void {
    this.addModal().close();
  }

  /**
   * Adds the selected skill from the modal to the profile.
   */
  submitAddSkill(): void {
    const name = this.normalizeSkillName(this.newSkillName.value);
    if (!name) {
      return;
    }
    if (this.hasSkill(name)) {
      this.duplicateSkill.emit();
      return;
    }
    this.patchSkills([
      ...this.skills(),
      { name, level: this.newSkillLevel.value },
    ]);
    this.closeAddModal();
  }

  /**
   * Updates the level of an existing skill.
   * @param name - Skill name
   * @param level - Selected level value
   */
  updateSkillLevel(name: string, level: string): void {
    this.patchSkills(
      this.skills().map((skill) =>
        skill.name === name
          ? { ...skill, level: level as ApplicantSkillLevel }
          : skill
      )
    );
  }

  /**
   * Removes a skill from the profile.
   * @param name - Skill name
   */
  removeSkill(name: string): void {
    this.patchSkills(this.skills().filter((skill) => skill.name !== name));
  }

  /**
   * Normalizes a free-text or selected skill name.
   * @param value - Raw skill value
   * @returns Trimmed skill name or empty string
   */
  private normalizeSkillName(value: string | null): string {
    return (value ?? '').toString().trim();
  }

  /**
   * Checks whether a skill name is already present.
   * @param name - Skill name
   * @returns Whether the skill exists
   */
  private hasSkill(name: string): boolean {
    return this.skills().some(
      (skill) => skill.name.toLowerCase() === name.toLowerCase()
    );
  }

  /**
   * Writes the skills array into the parent form.
   * @param skills - Next skills
   */
  private patchSkills(skills: PlatformProfileSkill[]): void {
    this.form().controls['skills'].setValue(skills);
    this.form().controls['skills'].markAsDirty();
  }
}
